import { Replay as ReplayIcon } from '@mui/icons-material';
import { IconButton, Tab, Tooltip } from '@mui/material';
import { AxiosError } from 'axios';
import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';

import {
  p2pProvidersApi,
  p2pProvidersPayinWebhooksApi,
  p2pProvidersPayoutWebhooksApi,
  providerTypesApi,
} from 'api';
import {
  CrudPage,
  DataGridColumnDefinition,
  DateLabel,
  EnabledStatusLabel,
  PageHeader,
  QueryTabPanel,
  QueryTabs,
} from 'components';
import {
  FilterDefinitionType,
  P2PProviderWebhookStatus,
  QueryKey,
} from 'enums';
import { useMutation, usePartialQuery } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  P2PProviderWebhook,
  PaginatedData,
  QueryParams,
} from 'types';
import { filtersUtils, formUtils, formatUtils } from 'utils';

enum P2PProviderWebhooksTab {
  PayinFailed = 'payin-failed',
  PayinProcessed = 'payin-processed',
  PayoutFailed = 'payout-failed',
  PayoutProcessed = 'payout-processed',
}

type WebhookFilters = {
  id?: string;
  providerId?: string;
  providerOrderId?: string;
  type?: string;
  from?: string;
  to?: string;
};

type WebhookKind = 'payin' | 'payout';

const getApi = (kind: WebhookKind) =>
  kind === 'payin'
    ? p2pProvidersPayinWebhooksApi
    : p2pProvidersPayoutWebhooksApi;

const getQueryKey = (kind: WebhookKind) =>
  kind === 'payin'
    ? QueryKey.P2PProviderPayinWebhooksList
    : QueryKey.P2PProviderPayoutWebhooksList;

type TabContentProps = {
  kind: WebhookKind;
  status: P2PProviderWebhookStatus;
};

const WebhooksTabContent: React.FC<TabContentProps> = ({ kind, status }) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_provider_webhooks',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const queryClient = useQueryClient();

  const api = getApi(kind);
  const queryKey = getQueryKey(kind);

  const queryResultProviders = useQuery(
    QueryKey.P2PProviders,
    p2pProvidersApi.getAll,
  );
  const queryResultProviderTypes = useQuery(
    QueryKey.ProviderTypes,
    providerTypesApi.getAll,
  );

  // `params.filters` is base64-encoded JSON: status must be merged into the decoded payload,
  // spreading the string would serialize it character by character.
  const getAllPaginatedWithStatus = useCallback(
    (
      params?: Partial<QueryParams>,
    ): Promise<PaginatedData<P2PProviderWebhook>> => {
      let parsed = {};
      try {
        parsed = params?.filters
          ? JSON.parse(filtersUtils.decode(params.filters))
          : {};
      } catch {
        parsed = {};
      }

      return api.getAllPaginated({
        ...params,
        filters: filtersUtils.encode(JSON.stringify({ ...parsed, status })),
      });
    },
    [api, status],
  );

  const queryResult = usePartialQuery<PaginatedData<P2PProviderWebhook>>(
    [queryKey, status],
    getAllPaginatedWithStatus,
    { useCachedCount: true },
  );

  const { mutate: retry, isLoading: isRetrying } = useMutation<
    unknown,
    AxiosError,
    string
  >(api.retry, {
    notifierType: 'save',
    onSuccess: () => {
      queryClient.invalidateQueries([[queryKey, status]]);
    },
  });

  const columns = useMemo(
    (): DataGridColumnDefinition<P2PProviderWebhook>[] => [
      {
        header: tCommon('filters.id'),
        valueGetter: (item) => item.id,
        valueClassName: 'tw-font-mono tw-text-xs',
      },
      {
        header: t('fields.created_at'),
        valueGetter: (item) => (
          <DateLabel>{formatUtils.formatDate(item.createdAt)}</DateLabel>
        ),
      },
      {
        header: t('fields.provider'),
        valueGetter: (item) => item.provider?.name || '-',
      },
      {
        header: t('fields.provider_type'),
        valueGetter: (item) => item.type,
      },
      {
        header: t('fields.ip'),
        valueGetter: (item) => item.ip,
      },
      {
        header: t('fields.status'),
        valueGetter: (item) => (
          <EnabledStatusLabel
            status={
              item.status === P2PProviderWebhookStatus.Processed
                ? 'active'
                : 'inactive'
            }
          />
        ),
      },
      {
        header: t('fields.error'),
        valueGetter: (item) =>
          item.error ? (
            <span className="tw-text-xs tw-text-red-600" title={item.error}>
              {item.error.length > 120
                ? `${item.error.slice(0, 120)}…`
                : item.error}
            </span>
          ) : (
            '-'
          ),
        valueClassName: 'tw-max-w-[280px]',
      },
      ...(status === P2PProviderWebhookStatus.Error
        ? [
            {
              header: t('fields.actions'),
              valueGetter: (item: P2PProviderWebhook) => (
                <Tooltip title={t('actions.retry')}>
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={isRetrying}
                      onClick={() => retry(item.id)}
                    >
                      <ReplayIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ),
            } as DataGridColumnDefinition<P2PProviderWebhook>,
          ]
        : []),
    ],
    [t, tCommon, status, isRetrying, retry],
  );

  const filtersDefinitions: FilterDefinition<WebhookFilters>[] = useMemo(
    () => [
      {
        label: tCommon('filters.id'),
        name: 'id',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
      {
        label: t('filters.provider'),
        name: 'providerId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(queryResultProviders.data || []),
      },
      {
        label: t('filters.provider_type'),
        name: 'type',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(
          queryResultProviderTypes.data || [],
          'name',
          'code',
        ),
      },
      {
        label: t('filters.provider_order_id'),
        name: 'providerOrderId',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
    ],
    [t, tCommon, queryResultProviders.data, queryResultProviderTypes.data],
  );

  return (
    <CrudPage
      filters={{ filtersDefinitions, withCommon: true }}
      table={{
        queryResult,
        columns,
        paginated: true,
      }}
    />
  );
};

export const P2PProviderWebhooksPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_provider_webhooks',
  });

  return (
    <Fragment>
      <PageHeader title={t('title')} divider={false} />
      <QueryTabs tabsEnum={P2PProviderWebhooksTab}>
        <Tab
          value={P2PProviderWebhooksTab.PayinFailed}
          label={t('tabs.payin_failed')}
        />
        <Tab
          value={P2PProviderWebhooksTab.PayinProcessed}
          label={t('tabs.payin_processed')}
        />
        <Tab
          value={P2PProviderWebhooksTab.PayoutFailed}
          label={t('tabs.payout_failed')}
        />
        <Tab
          value={P2PProviderWebhooksTab.PayoutProcessed}
          label={t('tabs.payout_processed')}
        />
      </QueryTabs>
      <QueryTabPanel value={P2PProviderWebhooksTab.PayinFailed}>
        <WebhooksTabContent
          kind="payin"
          status={P2PProviderWebhookStatus.Error}
        />
      </QueryTabPanel>
      <QueryTabPanel value={P2PProviderWebhooksTab.PayinProcessed}>
        <WebhooksTabContent
          kind="payin"
          status={P2PProviderWebhookStatus.Processed}
        />
      </QueryTabPanel>
      <QueryTabPanel value={P2PProviderWebhooksTab.PayoutFailed}>
        <WebhooksTabContent
          kind="payout"
          status={P2PProviderWebhookStatus.Error}
        />
      </QueryTabPanel>
      <QueryTabPanel value={P2PProviderWebhooksTab.PayoutProcessed}>
        <WebhooksTabContent
          kind="payout"
          status={P2PProviderWebhookStatus.Processed}
        />
      </QueryTabPanel>
    </Fragment>
  );
};
